<script setup lang="ts">
import { computed } from 'vue'
import { useRoute } from 'vue-router'

import { useAuth } from '@/composables/useAuth'
import { useAuthEffects } from '@/composables/useAuthEffects'
import { useAuthTabSync } from '@/composables/useAuthTabSync'
import { useIdleTimeout } from '@/composables/useIdleTimeout'
import { useLegacyBootstrap } from '@/composables/useLegacyBootstrap'
import SMediaDialog from '@/components/singleton/SMediaDialog/SMediaDialog.vue'
import SMessageDialog from '@/components/singleton/SMessageDialog/SMessageDialog.vue'
import SProfileDialog from '@/components/singleton/SProfileDialog/SProfileDialog.vue'
import SRespiteDialog from '@/components/singleton/SRespiteDialog/SRespiteDialog.vue'
import SAmortizationDialog from '@/components/singleton/SAmortizationDialog/SAmortizationDialog.vue'
import SPaymentDialog from '@/components/singleton/SPaymentDialog/SPaymentDialog.vue'
import SSnackbar from '@/components/singleton/SSnackbar/SSnackbar.vue'
// LEGACY components kept from the old App.vue. Deliberately excluded:
// - TheSnackbar — replaced by SSnackbar (its bus forwarder went with it, ADA-4457)
// - MediaDialog — replaced by SMediaDialog (ADA-4437 etapp 1)
// - MessageDialog — replaced by SMessageDialog (ADA-4437 etapp 2)
// - ProfileDialog — replaced by SProfileDialog (ADA-4437 etapp 3)
// - Respite — replaced by SRespiteDialog (ADA-4437 etapp 4)
// - Amortization — replaced by SAmortizationDialog (ADA-4437 etapp 5)
// - Payments — replaced by SPaymentDialog (ADA-4432); with it went the last
//   legacy dialog, the `payment` Vuex key and the actions_render_key bridge
// - Loader — died with the getDocuments fan-out (ADA-4435/D6): every view is
//   native or fetches its own data, so the app no longer boot-gates globally
import Navigation from '@old/components/navigation/index.vue'

const route = useRoute()

useLegacyBootstrap()
useIdleTimeout()
useAuthTabSync()
useAuthEffects()
const { isAuthenticated } = useAuth()

const loginView = computed(() => route.name === 'Authenticate')
</script>

<template>
  <v-app id="app">
    <!-- Unmounted (not just hidden) when logged out: the drawer is local state
         inside the legacy component and would otherwise stay open and clickable
         after an idle logout. Every route where the nav shows requires auth. -->
    <navigation v-if="isAuthenticated" />
    <v-main>
      <!-- pa-0 on the login route: the mobile photo strip is full-bleed (ADA-4497);
           the view owns its own paddings. -->
      <v-container
        :class="{ 'app-width': !loginView, 'fill-height': loginView, 'pa-0': loginView }"
        :fluid="loginView"
        class="position-relative"
      >
        <!-- Native singleton dialogs (ADA-4437): store-driven, closed on auth
             transitions via dialogsStore.reset() in useAuthEffects. Mount
             condition mirrors the legacy dialog it replaced. -->
        <s-media-dialog v-if="isAuthenticated" />
        <s-message-dialog v-if="isAuthenticated" />
        <s-profile-dialog v-if="isAuthenticated" />
        <s-respite-dialog v-if="isAuthenticated" />
        <s-amortization-dialog v-if="isAuthenticated" />

        <!-- NOT auth-gated, unlike its siblings: pay-with-OCR is reached from the login screen
             (ADA-4432 D4). Where the legacy Payments needed a `:key` remount to close itself on
             an auth transition, this one is store-driven — dialogsStore.reset() closes it. -->
        <s-payment-dialog />

        <router-view />
        <s-snackbar />
      </v-container>
    </v-main>
  </v-app>
</template>

<style lang="sass">
.v-main
    @media (max-width: 960px)
        flex: 0
</style>
